/**
 * @file Homepage.jsx
 * @description Homepage component including hero section and company introduction.
 */

import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import Slider from '../components/Slider';
import useScrollAnimation from '../hooks/useScrollAnimation';
import Image1 from '../new_images/pexels-vlada-karpovich-7433832.jpg';
import Image2 from '../new_images/pexels-mikhail-nilov-9304659.jpg';
import Image3 from '../new_images/pexels-tara-winstead-8386437.jpg';
import Image4 from '../new_images/pexels-mikhail-nilov-6930431.jpg';
import Image5 from '../new_images/pexels-yankrukov-7691673.jpg';
import Image6 from '../new_images/pexels-yankrukov-7691715.jpg';
import Image7 from '../new_images/pexels-vlada-karpovich-7433832.jpg';

const Homepage = () => {
  const whoWeAreRef = useScrollAnimation();
  const whatWeDoRef = useScrollAnimation();
  const howWeWorkRef = useScrollAnimation();
  const whoWeWorkWithRef = useScrollAnimation();
  const whatMakesUsDifferentRef = useScrollAnimation();
  const contactRef = useScrollAnimation();

  return (
    <div>
      <Helmet>
        <title>Bellefy Insights | Data For Social Impact</title>
        <meta
          name="description"
          content="Bellefy Insights is a mission-driven data and automation practice that helps NGOs, donors and public-sector bodies unlock the value of their data through cleaning, consolidation, cloud migration, and responsible AI."
        />
      </Helmet>

      {/* Hero / Intro Section */}
      <Slider />

      {/* Who We Are Section */}
      <section id="who-we-are" ref={whoWeAreRef} className="section-bg fade-in-on-scroll">
        <Container>
          <Row className="justify-content-center align-items-center">
            <Col md={6} className="mb-4 mb-md-0">
              <img src={Image1} alt="Data analytics and insights" className="img-fluid rounded shadow img-hover-effect" style={{ width: '100%', height: 'auto', objectFit: 'cover' }} />
            </Col>
            <Col md={6}>
              <h2 className="cormorant-garamond text-center mb-4 gradient-text">Who We Are</h2>
              <p className="lead text-center mb-3">
                Bellefy Insights is a mission-driven data and automation practice founded by practitioners who've managed complex datasets across development and government programs.
              </p>
              <p className="text-center mb-3">
                We envision a world where every organization leveraging data for social impact has the infrastructure to succeed. Bellefy Insights consolidates fragmented data sources, builds secure cloud platforms, creates dashboards, and deploys AI-powered analytics that automate repetitive work and unlock actionable insights—empowering non-profits, government agencies, and businesses to accelerate their mission and maximize their impact.
              </p>
              <p className="text-center">
                Beyond delivering tools, we prioritise adoption: we co-design with local teams, provide hands-on capacity building, and set up human-in-the-loop controls, audit trails and privacy safeguards so insights are trusted and decisions improve. Whether you need faster donor reporting or smarter service delivery, our team turns trusted data and pragmatic automation into measurable impact.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* What We Do Section */}
      <section id="what-we-do" ref={whatWeDoRef} className="fade-in-on-scroll">
        <Container>
          <Row className="justify-content-center">
            <Col md={12}>
              <h2 className="cormorant-garamond text-center gradient-text">Our Services</h2>
              <p className="text-center mb-5 lead">
                At Bellefy Insights, we are committed to transforming information management and cloud migration, enabling organizations to unlock the full potential of their data. We work closely with stakeholders to ensure accuracy, consistency, and relevance in every dataset. From data collection using traditional surveys, satellite imagery, chatbots, and apps for data collection to creating digital data infrastructures in the cloud, to harnessing the power of AI, intelligent agents, and large language models—we turn data into actionable insights.
              </p>
              <Row>
                <Col md={6} className="mb-4">
                  <div className="tech-card">
                    <img src={Image2} alt="Information Management" className="img-fluid rounded mb-3 img-hover-effect" style={{ width: '100%', height: '260px', objectFit: 'cover' }} />
                    <h4>Information Management</h4>
                    <p>Data chaos slows down impact. We help organizations cut through the noise by building systems that bring all your data together in one place you can trust. From strategy to execution, we design workflows that keep information accurate, accessible, and meaningful so your team spends less time chasing numbers and more time driving change. We not only tailor solutions to enhance the availability and reliability of data, but we also streamline response efforts by leveraging cutting-edge technology to maximize impact.</p>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className="tech-card">
                    <img src={Image3} alt="Data Collection and Analysis" className="img-fluid rounded mb-3 img-hover-effect" style={{ width: '100%', height: '260px', objectFit: 'cover' }} />
                    <h4>Data Collection, Transformation, and Analysis</h4>
                    <p>Good decisions start with good data. We work with your team on metrics mapping, design data collection tools using Kobo Toolbox, Deedose, chatbots, satellite imagery, remote sensing, or traditional manual data entry from the field where we hire enumerators for field surveys. Our team works on the whole data processing process from cleaning, integration to databases in cloud architecture, analysis and visualization using business intelligence tools such as Power BI, Tableau, Looker, Excel or Informatica.</p>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className="tech-card">
                    <img src={Image4} alt="Monitoring and Evaluation" className="img-fluid rounded mb-3 img-hover-effect" style={{ width: '100%', height: '260px', objectFit: 'cover' }} />
                    <h4>Monitoring and Evaluation</h4>
                    <p>Measuring impact doesn't have to be complicated. We collaborate to create monitoring and evaluation frameworks that suit your team's needs, from baselines to endline evaluations. Together, we'll develop clear Theories of Change and practical frameworks that align with your mission, donors, and the communities you serve. Our goal is to help you track what truly matters and demonstrate real value of your work. Our support ensures that evidence drives better decision-making, program improvements, and sustainable impact.</p>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className="tech-card">
                    <img src={Image1} alt="Digital Transformation and Cloud Computing" className="img-fluid rounded mb-3 img-hover-effect" style={{ width: '100%', height: '260px', objectFit: 'cover' }} />
                    <h4>Digital Transformation and Cloud Computing</h4>
                    <p>Is your team up to date on the new developments in AI? Is your data still stored in spreadsheets that are scattered among different team members or in files? That is where we come through for you. We integrate automation, cloud technologies, and AI-driven tools that reduce costs, speed up reporting timelines and have a unified database that is easy to interact with. Our team further deploys machine learning technologies such as generative AI and AI agents to automate the reporting process.</p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How We Work Section */}
      <section id="how-we-work" ref={howWeWorkRef} className="section-bg fade-in-on-scroll">
        <Container>
          <Row className="justify-content-center align-items-center">
            <Col md={5} className="mb-4 mb-md-0">
              <img src={Image5} alt="How we work" className="img-fluid rounded shadow img-hover-effect float-animation" style={{ width: '100%', height: 'auto', objectFit: 'cover' }} />
            </Col>
            <Col md={7}>
              <h2 className="cormorant-garamond text-center mb-4 gradient-text">How We Work</h2>
              <p className="text-center mb-4">
                Beyond delivering tools, we prioritise adoption and sustainability:
              </p>
              <Row className="mt-4">
                <Col md={6} className="mb-3">
                  <div className="tech-card" style={{ padding: '20px', marginBottom: '15px' }}>
                    <h5 style={{ color: '#ed7b0b' }}>Co-Design with Local Teams</h5>
                    <p>We work alongside your team to ensure solutions fit your context and capacity</p>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="tech-card" style={{ padding: '20px', marginBottom: '15px' }}>
                    <h5 style={{ color: '#ed7b0b' }}>Hands-On Capacity Building</h5>
                    <p>We provide training and support to ensure your team can use and maintain systems long-term</p>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="tech-card" style={{ padding: '20px', marginBottom: '15px' }}>
                    <h5 style={{ color: '#ed7b0b' }}>Responsible AI & Privacy Safeguards</h5>
                    <p>We set up human-in-the-loop controls, audit trails and privacy safeguards so insights are trusted</p>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="tech-card" style={{ padding: '20px', marginBottom: '15px' }}>
                    <h5 style={{ color: '#ed7b0b' }}>End-to-End Implementation</h5>
                    <p>From data ingestion and modeling through dashboards and performance frameworks, we take full ownership</p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Who We Work With Section */}
      <section id="who-we-work-with" ref={whoWeWorkWithRef} className="fade-in-on-scroll">
        <Container>
          <Row className="justify-content-center">
            <Col md={10}>
              <h2 className="cormorant-garamond text-center gradient-text">Who We Work With</h2>
              <p className="text-center mb-4">
                Our team has extensively worked in tech consulting and for INGOs in the humanitarian and non-profit sectors. We work with:
              </p>
              <Row className="mt-4 align-items-center">
                <Col md={4} className="mb-4">
                  <div className="tech-card">
                    <img src={Image6} alt="NGOs and Development" className="img-fluid rounded mb-3 img-hover-effect" style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                    <h5 style={{ color: '#ed7b0b' }}>NGOs & Development Organizations</h5>
                    <p>Modernizing data and M&E systems, faster donor reporting, and smarter service delivery</p>
                  </div>
                </Col>
                <Col md={4} className="mb-4">
                  <div className="tech-card">
                    <img src={Image7} alt="Public Sector" className="img-fluid rounded mb-3 img-hover-effect" style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                    <h5 style={{ color: '#ed7b0b' }}>Donors & Public-Sector Bodies</h5>
                    <p>Managing complex datasets across development and government programs</p>
                  </div>
                </Col>
                <Col md={4} className="mb-4">
                  <div className="tech-card">
                    <img
                      src={Image5}
                      alt="Humanitarian Organizations"
                      className="img-fluid rounded mb-3 img-hover-effect"
                      style={{ width: '100%', height: '180px', objectFit: 'cover' }}
                    />
                    <h5 style={{ color: '#ed7b0b' }}>Humanitarian Organizations</h5>
                    <p>Building systems that support clarity, accountability, and measurable impact</p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* What Makes Us Different Section */}
      <section id="what-makes-us-different" ref={whatMakesUsDifferentRef} className="section-bg fade-in-on-scroll">
        <Container>
          <Row className="justify-content-center">
            <Col md={10}>
              <h2 className="cormorant-garamond text-center gradient-text">What Makes Us Different</h2>
              <p className="text-center lead mt-4">
                We go beyond analysis and reporting to implement systems that embed insight into everyday operations. Success is measured by use, not deliverables.
              </p>
              <p className="text-center mt-4">
                Our vision is a future where data is not just stored, but actively drives organizational success, innovation, and continuous improvement. We turn trusted data and pragmatic automation into measurable impact.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Section */}
      <section id="contact" ref={contactRef} className="fade-in-on-scroll">
        <Container>
          <Row className="justify-content-center">
            <Col md={8}>
              <div className="tech-card" style={{ textAlign: 'center' }}>
                <h2 className="cormorant-garamond gradient-text">Contact</h2>
                <div className="mt-4">
                  <p><strong>Bellefy Insights</strong></p>
                  <p>Website: <a href="https://www.bellefyinsights.org" target="_blank" rel="noopener noreferrer" style={{ color: '#ed7b0b', fontWeight: 600 }}>www.bellefyinsights.org</a></p>
                  <p>Email: <a href="mailto:contact@bellefyinsights.com" style={{ color: '#ed7b0b', fontWeight: 600 }}>contact@bellefyinsights.com</a></p>
                  <p className="mt-3">
                    <a href="https://www.linkedin.com/company/bellefy-insights" target="_blank" rel="noopener noreferrer" style={{ color: '#ed7b0b', fontWeight: 600 }} aria-label="Bellefy Insights on LinkedIn">
                      LinkedIn
                    </a>
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Homepage;
